// File: /var/www/MissionaryIndia/app1/frontend/src/components/SupportAdmin.js

import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/SupportAdmin.css";

function SupportAdmin({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [response, setResponse] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [respondingTo, setRespondingTo] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get("/api/support-requests");
      setRequests(response.data.requests);
    } catch (error) {
      console.error("Failed to fetch support requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus) => {
    if (!window.confirm(`Change status to ${newStatus.replace('_', ' ')}?`)) {
      return;
    }

    setUpdating(true);
    try {
      await axios.patch(`/api/support-requests/${requestId}`, { status: newStatus });
      alert("Status updated successfully!");
      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      alert("Failed to update status: " + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmitResponse = async (requestId) => {
    if (!response.trim()) {
      alert("Please enter a response");
      return;
    }

    setUpdating(true);
    try {
      await axios.patch(`/api/support-requests/${requestId}`, {
        admin_response: response,
        status: "in_progress"
      });
      alert("Response submitted successfully!");
      setResponse("");
      setRespondingTo(null);
      fetchRequests();
    } catch (error) {
      alert("Failed to submit response: " + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkComplete = async (requestId) => {
    if (!window.confirm("Mark this request as completed?")) {
      return;
    }

    setUpdating(true);
    try {
      await axios.patch(`/api/support-requests/${requestId}`, { status: "completed" });
      alert("Request marked as completed!");
      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      alert("Failed to mark as completed: " + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this support request? This action cannot be undone.")) {
      return;
    }

    setUpdating(true);
    try {
      await axios.delete(`/api/support-requests/${requestId}`);
      alert("Support request deleted!");
      fetchRequests();
      setSelectedRequest(null);
    } catch (error) {
      alert("Failed to delete request: " + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      open: "status-open",
      in_progress: "status-in-progress",
      completed: "status-completed",
      closed: "status-closed"
    };
    return (
      <span className={`status-badge ${statusColors[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getTypeLabel = (type) => {
    const types = {
      bug: "Bug Report",
      feature: "Feature Request",
      change: "Change Request",
      other: "Other"
    };
    return types[type] || "Other";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const filteredRequests = statusFilter === "all"
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const statusCounts = {
    all: requests.length,
    open: requests.filter(r => r.status === 'open').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="support-admin-container">
      <div className="admin-header">
        <h2>Support Requests Admin</h2>
        <div className="admin-stats">
          <div className="stat-item">Total: {requests.length}</div>
          <div className="stat-item open">Open: {statusCounts.open}</div>
          <div className="stat-item in-progress">In Progress: {statusCounts.in_progress}</div>
        </div>
      </div>

      <div className="filter-bar">
        <button
          className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          All ({statusCounts.all})
        </button>
        <button
          className={`filter-btn ${statusFilter === 'open' ? 'active' : ''}`}
          onClick={() => setStatusFilter('open')}
        >
          Open ({statusCounts.open})
        </button>
        <button
          className={`filter-btn ${statusFilter === 'in_progress' ? 'active' : ''}`}
          onClick={() => setStatusFilter('in_progress')}
        >
          In Progress ({statusCounts.in_progress})
        </button>
        <button
          className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
          onClick={() => setStatusFilter('completed')}
        >
          Completed ({statusCounts.completed})
        </button>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="no-requests">
          No {statusFilter !== 'all' ? statusFilter.replace('_', ' ') : ''} support requests found.
        </div>
      ) : (
        <div className="admin-requests-list">
          {filteredRequests.map((request) => (
            <div key={request.id} className="admin-request-card">
              <div className="request-main">
                <div className="request-header">
                  <div className="request-title-row">
                    <h3>{request.subject}</h3>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="request-meta-row">
                    <span className="request-type">{getTypeLabel(request.type)}</span>
                    <span className="request-user">By: {request.user_full_name || request.user_name}</span>
                    <span className="request-date">Submitted: {formatDate(request.created_at)}</span>
                  </div>
                </div>

                <div className="request-description">
                  {request.description}
                </div>

                {request.admin_response && (
                  <div className="existing-response">
                    <div className="response-label">
                      <strong>Your Response:</strong>
                      {request.responded_at && (
                        <span> ({formatDate(request.responded_at)})</span>
                      )}
                    </div>
                    <div className="response-text">{request.admin_response}</div>
                  </div>
                )}

                <div className="request-actions">
                  {request.status === 'open' && (
                    <button
                      onClick={() => setRespondingTo(respondingTo === request.id ? null : request.id)}
                      className="respond-btn"
                    >
                      {respondingTo === request.id ? "Cancel Response" : "Respond"}
                    </button>
                  )}

                  {request.status === 'in_progress' && (
                    <button
                      onClick={() => handleMarkComplete(request.id)}
                      className="complete-btn"
                      disabled={updating}
                    >
                      Mark Complete
                    </button>
                  )}

                  {request.status !== 'completed' && (
                    <button
                      onClick={() => handleUpdateStatus(request.id, 'in_progress')}
                      className="status-btn"
                      disabled={updating || request.status === 'in_progress'}
                    >
                      Mark In Progress
                    </button>
                  )}

                  <button
                    onClick={() => handleDelete(request.id)}
                    className="delete-btn"
                    disabled={updating}
                  >
                    Delete
                  </button>
                </div>

                {respondingTo === request.id && (
                  <div className="response-form">
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Enter your response to the user..."
                      rows="6"
                      className="response-textarea"
                    />
                    <div className="response-actions">
                      <button
                        onClick={() => handleSubmitResponse(request.id)}
                        className="submit-response-btn"
                        disabled={updating || !response.trim()}
                      >
                        {updating ? "Submitting..." : "Submit Response"}
                      </button>
                      <button
                        onClick={() => {
                          setRespondingTo(null);
                          setResponse("");
                        }}
                        className="cancel-response-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SupportAdmin;
